import {
  Badge,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@paguz/ui';
import { useState } from 'react';
import type { AuditEntry } from '../../api/admin-schemas.ts';
import { CopyValue, DataList, JsonBlock } from '../../components/page.tsx';
import { formatDateTime, shortId } from '../../lib/format.ts';

const ACTOR_LABEL: Readonly<Record<AuditEntry['actor']['type'], string>> = {
  USER: 'Usuario',
  CUSTOMER: 'Cliente',
  AGENT: 'Agente IA',
  SYSTEM: 'Sistema',
};
const ACTOR_TONE = {
  USER: 'default',
  CUSTOMER: 'secondary',
  AGENT: 'highlight',
  SYSTEM: 'outline',
} as const;

/** Tabla de registros de auditoría con detalle (antes/después, hash) en hoja lateral. */
export function AuditTable({ entries }: { entries: readonly AuditEntry[] }) {
  const [selected, setSelected] = useState<AuditEntry | null>(null);
  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-44">Fecha</TableHead>
            <TableHead>Actor</TableHead>
            <TableHead>Acción</TableHead>
            <TableHead>Entidad</TableHead>
            <TableHead>Motivo</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.map((entry) => (
            <TableRow
              key={entry.id}
              tabIndex={0}
              className="cursor-pointer"
              onClick={() => {
                setSelected(entry);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') setSelected(entry);
              }}
            >
              <TableCell className="whitespace-nowrap text-muted-foreground">
                {formatDateTime(entry.at)}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Badge variant={ACTOR_TONE[entry.actor.type]}>
                    {ACTOR_LABEL[entry.actor.type]}
                  </Badge>
                  <span className="font-medium">{entry.actor.name}</span>
                </div>
              </TableCell>
              <TableCell>
                <code className="font-mono text-xs">{entry.action}</code>
              </TableCell>
              <TableCell className="text-muted-foreground">
                {entry.entityType}
                {entry.entityId && (
                  <span className="ml-1 font-mono text-xs">{shortId(entry.entityId, 6, 4)}</span>
                )}
              </TableCell>
              <TableCell className="max-w-56 truncate">{entry.reason ?? '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Sheet
        open={selected !== null}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      >
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle>Registro #{selected.seq}</SheetTitle>
                <SheetDescription>
                  <code className="font-mono">{selected.action}</code>
                </SheetDescription>
              </SheetHeader>
              <div className="flex flex-col gap-5 px-4 pb-6">
                <DataList
                  items={[
                    { label: 'Cuándo', value: formatDateTime(selected.at) },
                    {
                      label: 'Quién',
                      value: `${selected.actor.name} (${ACTOR_LABEL[selected.actor.type]})`,
                    },
                    {
                      label: 'Desde dónde',
                      value: selected.ip ? `${selected.ip} · ${selected.device ?? ''}` : '—',
                    },
                    {
                      label: 'Entidad',
                      value: `${selected.entityType} ${selected.entityId ?? ''}`,
                    },
                    { label: 'Motivo', value: selected.reason ?? '—' },
                    {
                      label: 'traceId',
                      value: <CopyValue value={selected.traceId} label="traceId" />,
                    },
                  ]}
                />
                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <p className="mb-1 text-xs font-semibold text-muted-foreground uppercase">
                      Antes
                    </p>
                    <JsonBlock value={selected.before} />
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-semibold text-muted-foreground uppercase">
                      Después
                    </p>
                    <JsonBlock value={selected.after} />
                  </div>
                </div>
                <DataList
                  columns={1}
                  items={[
                    {
                      label: 'Hash',
                      value: (
                        <CopyValue
                          value={selected.hash}
                          label="hash"
                          display={shortId(selected.hash, 16, 8)}
                        />
                      ),
                    },
                    {
                      label: 'Hash anterior',
                      value: (
                        <CopyValue
                          value={selected.prevHash}
                          label="hash anterior"
                          display={
                            selected.prevHash ? shortId(selected.prevHash, 16, 8) : undefined
                          }
                        />
                      ),
                    },
                  ]}
                />
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
